import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, BookOpen, Loader2, Maximize2, Minimize2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const LIBRARY_BOOKS = [
  { level: 'A1.1', books: [
    { id: 'menschen_a11_kursbuch', label: 'Kursbuch', icon: '📕', page: '/menschen-a1' },
    { id: 'menschen_a11_arbeitsbuch', label: 'Arbeitsbuch', icon: '📗', page: '/menschen-a1' },
  ]},
  { level: 'A1.2', books: [
    { id: 'menschen_a12_kursbuch', label: 'Kursbuch', icon: '📕', page: '/menschen-a12' },
    { id: 'menschen_a12_arbeitsbuch', label: 'Arbeitsbuch', icon: '📗', page: '/menschen-a12' },
  ]},
  { level: 'A2.1', books: [
    { id: 'menschen_a21_kursbuch', label: 'Kursbuch', icon: '📕', page: '/menschen-a21' },
    { id: 'menschen_a21_arbeitsbuch', label: 'Arbeitsbuch', icon: '📗', page: '/menschen-a21' },
  ]},
  { level: 'A2.2', books: [
    { id: 'menschen_a22_kursbuch', label: 'Kursbuch', icon: '📕', page: '/menschen-a22' },
    { id: 'menschen_a22_arbeitsbuch', label: 'Arbeitsbuch', icon: '📗', page: '/menschen-a22' },
  ]},
];

export default function Bibliothek() {
  const [activeBook, setActiveBook] = useState(null);
  const [bookUrl, setBookUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  const loadBook = async (book) => {
    setActiveBook(book);
    setBookUrl(null);
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/books/${book.id}/url`);
      const data = await res.json();
      setBookUrl(data.viewer_url || null);
    } catch {
      setBookUrl(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a' }} data-testid="bibliothek-page">
      <style>{`
        .lib-header { position: sticky; top: 0; z-index: 50; background: rgba(15,23,42,0.95); backdropFilter: blur(12px); borderBottom: 1px solid #1e293b; }
        .lib-grid { display: grid; gridTemplateColumns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px; }
        .lib-card { background: #1e293b; border: 1px solid #334155; borderRadius: 12px; padding: 20px; cursor: pointer; transition: all 0.2s; }
        .lib-card:hover { borderColor: #f59e0b; transform: translateY(-2px); boxShadow: 0 8px 24px rgba(0,0,0,0.3); }
        .lib-card.active { borderColor: #f59e0b; background: rgba(245,158,11,0.08); }
        .lib-level { fontSize: 12px; fontWeight: 700; letterSpacing: 1px; textTransform: uppercase; color: #94a3b8; marginBottom: 16px; paddingBottom: 8px; borderBottom: 1px solid #334155; }
      `}</style>

      {/* Header */}
      <div className="lib-header">
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Link to="/dashboard" style={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px', textDecoration: 'none', fontSize: '14px' }} data-testid="back-to-dashboard">
              <ChevronLeft className="h-5 w-5" /> Back
            </Link>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <BookOpen className="h-5 w-5" style={{ color: '#f59e0b' }} />
              <h1 style={{ fontWeight: 700, fontSize: '18px', color: '#e2e8f0', margin: 0 }}>Bibliothek / Library</h1>
            </div>
          </div>
          {activeBook && (
            <button onClick={() => { setActiveBook(null); setBookUrl(null); setFullscreen(false); }}
              style={{ padding: '6px 16px', borderRadius: '8px', background: '#334155', color: '#e2e8f0', border: 'none', cursor: 'pointer', fontSize: '13px' }}
              data-testid="close-viewer">
              Alle Bücher / All Books
            </button>
          )}
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 16px' }}>
        {/* Book Grid */}
        {!activeBook && (
          <div>
            <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '24px' }}>
              8 interaktive Bücher · 8 Interactive Books · 8 كتب تفاعلية
            </p>
            {LIBRARY_BOOKS.map((group) => (
              <div key={group.level} style={{ marginBottom: '28px' }}>
                <div className="lib-level">Menschen {group.level}</div>
                <div className="lib-grid">
                  {group.books.map((book) => (
                    <div key={book.id} className="lib-card" onClick={() => loadBook(book)} data-testid={`book-card-${book.id}`}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                        <span style={{ fontSize: '28px' }}>{book.icon}</span>
                        <div>
                          <p style={{ fontWeight: 600, fontSize: '15px', color: '#e2e8f0', margin: 0 }}>
                            Menschen {group.level}
                          </p>
                          <p style={{ fontSize: '13px', color: '#f59e0b', margin: '2px 0 0', fontWeight: 500 }}>
                            {book.label}
                          </p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#64748b', fontSize: '12px' }}>
                        <BookOpen className="h-3 w-3" /> Interaktive digitale Ausgabe
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Book Viewer */}
        {activeBook && (
          <div className={fullscreen ? 'pdf-fullscreen' : ''} style={fullscreen ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, background: '#0f172a' } : {}}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', background: 'linear-gradient(135deg, #1e293b, #0f172a)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: fullscreen ? 0 : '12px 12px 0 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '24px' }}>{activeBook.icon}</span>
                <div>
                  <p style={{ fontWeight: 700, fontSize: '16px', color: '#e2e8f0', margin: 0 }}>
                    Menschen {LIBRARY_BOOKS.find(g => g.books.some(b => b.id === activeBook.id))?.level} – {activeBook.label}
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setFullscreen(!fullscreen)} style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} data-testid="viewer-fullscreen">
                  {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </button>
                {bookUrl && (
                  <a href={bookUrl} target="_blank" rel="noopener noreferrer" style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }} data-testid="viewer-new-tab">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  </a>
                )}
              </div>
            </div>

            {loading && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', background: '#0f172a', border: '1px solid rgba(245,158,11,0.2)', borderTop: 'none', borderRadius: '0 0 12px 12px' }}>
                <Loader2 className="h-10 w-10 animate-spin" style={{ color: '#f59e0b', marginBottom: '16px' }} />
                <p style={{ color: '#e2e8f0', fontWeight: 600 }}>Buch wird geladen...</p>
              </div>
            )}

            {bookUrl && !loading && (
              <div style={{ border: '1px solid rgba(245,158,11,0.2)', borderTop: 'none', borderRadius: fullscreen ? 0 : '0 0 12px 12px', overflow: 'hidden' }}>
                <iframe src={bookUrl} title={`${activeBook.label}`} style={{ width: '100%', height: fullscreen ? 'calc(100vh - 64px)' : '78vh', border: 'none', display: 'block' }} allow="fullscreen" data-testid="book-iframe" />
              </div>
            )}

            {!bookUrl && !loading && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px', background: '#0f172a', border: '1px solid rgba(245,158,11,0.2)', borderTop: 'none', borderRadius: '0 0 12px 12px' }}>
                <p style={{ color: '#f87171', fontWeight: 600, marginBottom: '12px' }}>Buch konnte nicht geladen werden.</p>
                <button onClick={() => loadBook(activeBook)} style={{ padding: '10px 24px', borderRadius: '8px', background: '#f59e0b', color: '#0f172a', fontWeight: 600, border: 'none', cursor: 'pointer' }}>
                  Erneut versuchen / Retry
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
